// client/src/writer/hooks/useFileAndCoverActions.ts
import { useCallback, ChangeEvent } from 'react';
import { Editor } from '@tiptap/react'; 
import StarterKit from '@tiptap/starter-kit'; 
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/queryClient';
import { cacheManager } from '../../utils/cacheUtils';
import { useToast } from '../../hooks/use-toast';

import type { ProjectState } from '../types/project';
import type { BookMetadata, WorldBuildingData, ChapterVitals, MainView, LoadingStates, PublishingEssentials, FrontMatter, BackMatter, AuthorDetails, AppendixItem, GlossaryTerm, BibliographyEntry } from '../types';
import { getActiveVersion } from '../utils/chapterUtils';
import { getTargetWordCount } from '../utils/projectUtils';
import { normalizeBookType } from '../utils/bookTypeUtils';
import { markdownToHtml } from '../utils/markdownToHtml';
import { generatePdf } from '../utils/generatePdf';
import { generateEpub } from '../utils/generateEpub';
import { generateAndDownloadKindleEpub } from '../utils/generateKindleEpub';
import { initialProjectState, initialChapterVitals, initialAuthorDetails, initialFrontMatter, initialBackMatter, initialPublishingEssentials, LOCAL_STORAGE_ACTIVE_PROJECT_KEY } from '../constants';
import { CoreState } from './useCoreState';
import { ModalControls } from './useModalStates';
import { loadInitialProjectState } from '../utils/projectStateUtils';
import { ConfirmationLogic } from './useConfirmation';
import { getEnhancedImagePrompt, BookFormatKey, ImageTypeKey, getRecommendedFormat } from '../utils/bookImageSpecs';

// Enhanced image generation options interface
export interface ImageGenerationOptions {
    imageType?: ImageTypeKey;
    bookFormat?: BookFormatKey;
    designStyle?: 'minimalist' | 'ornate' | 'modern' | 'classic' | 'artistic';
    colorScheme?: 'monochrome' | 'duotone' | 'full-color' | 'warm' | 'cool';
    mood?: string;
}

export interface FileAndCoverActions {
    handleSaveApiKey: () => void;
    handleGenerateCover: (prompt: string) => Promise<void>;
    handleGenerateBookImage: (prompt: string, options?: ImageGenerationOptions) => Promise<void>; // 🆕 Enhanced image generation
    handleExportMarkdown: () => void;
    handleExportJson: () => void;
    handleImportJson: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
    handleBuildForPublishing: () => void;
    handleExportKindle: () => Promise<void>;
    handleSaveProject: () => Promise<void>;
    handleDeleteProject: () => Promise<void>;
}

export const useFileAndCoverActions = (
    coreState: CoreState,
    modalControls: ModalControls,
    confirmationLogic: ConfirmationLogic
): FileAndCoverActions => {
    const {
        projectState, apiKey, setApiKey, openaiApiKey, setOpenaiApiKey, deepseekApiKey, setDeepseekApiKey,
        setLoadingState, setError, isInitialBlueprintReady, setMainView, determineInitialViewBasedOnState, 
        setProjectState 
    } = coreState;
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const aiMutation = useMutation({
        mutationFn: async ({ task, payload }: { task: string, payload: any }) => {
            const response = await apiRequest(`/api/llm/${task}`, "POST", payload);
            return response.json();
        },
        onSuccess: async (data, variables) => {
            const { task, payload } = variables;
            let successMessage = "AI action successful!";
            
            if (task === 'image-generate') { // 🔧 FIXED: Match corrected task name
                const base64Image = data.data;
                const imageUrl = `data:image/jpeg;base64,${base64Image}`;
                modalControls.setGeneratedImageDataUrl(imageUrl); 
                successMessage = "Cover image generated.";
            }
            toast({ title: "Success", description: successMessage });

            // CRITICAL FIX: Fetch and update progress after image generation
            if (projectState?.projectId) {
                try {
                    const progressRaw = await apiRequest(`/api/story-projects/${projectState.projectId}/summary`, 'GET');
                    const progressResponse = await progressRaw.json();
                    if (progressResponse && progressResponse.progress) {

                        // Update project summary cache with fresh progress
                        queryClient.setQueryData(['story-project-summary', projectState.projectId], (oldSummary: any) => {
                            if (oldSummary) {
                                return {
                                    ...oldSummary,
                                    progress: progressResponse.progress,
                                    updatedAt: new Date().toISOString()
                                };
                            }
                            return progressResponse;
                        });

                        // Update project-summary localStorage cache
                        cacheManager.set('project-summary', projectState.projectId, {
                            ...progressResponse,
                        }, 5 * 60 * 1000);

                    }
                } catch (error) {
                    console.warn('Failed to fetch updated progress after image generation:', error);
                }
            }
        },
        onError: (error: any, variables) => {
            setError(`AI Error: ${error.message}`);
            toast({ title: "AI Error", description: error.message, variant: "destructive" });
            if (error.rawResponse) modalControls.openDebugResponseModal(error.rawResponse);
        },
        onSettled: (data, error, variables) => {
            const { task } = variables;
            if (task === 'image-generate') setLoadingState('cover', false); // 🔧 FIXED: Match corrected task name
        }
    });

    const handleSaveApiKey = useCallback(() => {
        setApiKey(modalControls.apiKeyInputValue);
        setOpenaiApiKey(modalControls.openaiApiKeyInputValue);
        setDeepseekApiKey(modalControls.deepseekApiKeyInputValue);
        modalControls.closeApiKeyModal();
        setError(null);
    }, [modalControls, setApiKey, setOpenaiApiKey, setDeepseekApiKey, setError]);

    const handleGenerateCover = useCallback(async (prompt: string) => { 
        if (!apiKey) { setError("Please set API Key."); modalControls.openApiKeyModal(apiKey); return; }
        if (!prompt.trim()) { setError("Please enter a prompt for the cover art."); return; }
        
        setLoadingState('cover', true); 
        setError(null); 
        modalControls.setGeneratedImageDataUrl(null); 
        
        aiMutation.mutate({
            task: 'image-generate', // 🔧 FIXED: Use correct endpoint name to match server routes
            payload: {
                prompt,
                bookMetadata: projectState?.bookMetadata,
                worldBuildingData: projectState?.blueprint?.worldBuildingSuggestions,
                outlineIdea: projectState?.outlineIdea,
            }
        });
    }, [apiKey, setLoadingState, setError, modalControls, aiMutation, projectState]);

    // 🆕 ENHANCED: Professional book image generation with format specifications
    const handleGenerateBookImage = useCallback(async (prompt: string, options: ImageGenerationOptions = {}) => {
        if (!apiKey) { setError("Please set API Key."); modalControls.openApiKeyModal(apiKey); return; }
        if (!prompt.trim()) { setError("Please enter a prompt for the book image."); return; }

        // Determine book format - use specified format, infer from book type, or default to 6x9
        const bookFormat = options.bookFormat ||
                          getRecommendedFormat(projectState?.bookMetadata?.genreSubject || 'novel') ||
                          '6x9';

        // Default image type to cover image if not specified
        const imageType = options.imageType || 'coverImage';

        // Generate enhanced prompt with book specifications
        const enhancedPrompt = getEnhancedImagePrompt(
            imageType,
            bookFormat,
            prompt,
            {
                designStyle: options.designStyle || 'artistic',
                colorScheme: options.colorScheme || 'full-color',
                mood: options.mood || projectState?.bookMetadata?.toneAndMood
            }
        );

        console.log('🎨 [BOOK-IMAGE-GENERATION] Enhanced prompt generated:', {
            originalPrompt: prompt,
            imageType,
            bookFormat,
            designStyle: options.designStyle,
            colorScheme: options.colorScheme,
            enhancedPromptLength: enhancedPrompt.length
        });

        setLoadingState('cover', true);
        setError(null);
        modalControls.setGeneratedImageDataUrl(null);

        aiMutation.mutate({
            task: 'image-generate',
            payload: {
                prompt: enhancedPrompt, // Use enhanced prompt with specifications
                bookMetadata: projectState?.bookMetadata,
                worldBuildingData: projectState?.blueprint?.worldBuildingSuggestions,
                outlineIdea: projectState?.outlineIdea,
                imageType,
                bookFormat,
                designOptions: options
            }
        });
    }, [apiKey, setLoadingState, setError, modalControls, aiMutation, projectState]);

    const formatListForMarkdown = (items: any[], type: 'appendices' | 'glossary' | 'bibliography'): string => {
        if (!items || items.length === 0) return '';
        let markdown = '';
        switch (type) {
            case 'appendices':
                items.forEach((item: AppendixItem) => {
                    markdown += `#### ${item.title || 'Untitled Appendix'}\n\n${item.content}\n\n---\n\n`;
                });
                break;
            case 'glossary':
                items.forEach((item: GlossaryTerm) => {
                    markdown += `**${item.term || 'N/A'}:** ${item.definition}\n\n`;
                });
                break;
            case 'bibliography':
                items.forEach((item: BibliographyEntry) => {
                    markdown += `- ${item.citation}\n`;
                });
                markdown += '\n';
                break;
        }
        return markdown;
    };

    const buildMarkdownManuscript = useCallback(async (projectState: ProjectState | null): Promise<string> => {
        if (!projectState) return '';

        let markdownOutput = `# ${String(projectState.blueprint.worldBuildingSuggestions.selectedStoryTitle || projectState.currentProjectName || projectState.outlineIdea || 'My Work')}\n\n`;
        
        const ad = projectState.authorDetails;
        if (ad.authorName) markdownOutput += `## By ${ad.authorName}\n\n`;
        if (ad.authorProfileImageBase64) markdownOutput += `[Author Profile Image was present in project]\n\n`;
        
        // --- FRONT MATTER ---
        markdownOutput += "---\n## Front Matter\n---\n\n";
        const fm = projectState.frontMatter;
        if(fm.titlePageContent) markdownOutput += `### Title Page (Details)\n\n${fm.titlePageContent}\n\n`;
        if(fm.titlePageImageBase64) markdownOutput += `[Title Page Image was present in project]\n\n`;
        if(fm.copyrightPageContent) markdownOutput += `### Copyright Page\n\n${fm.copyrightPageContent}\n\n`;
        if(fm.dedication) markdownOutput += `### Dedication\n\n${fm.dedication}\n\n`;
        if(fm.epigraph) markdownOutput += `### Epigraph\n\n${fm.epigraph}\n\n`;
        if(fm.tableOfContents && fm.tableOfContents.trim() !== '<!-- Table of Contents will be generated here -->') {
            markdownOutput += `### Table of Contents\n\n${fm.tableOfContents}\n\n`;
        }
        if(fm.foreword) markdownOutput += `### Foreword${fm.forewordBy ? ` (by ${fm.forewordBy})` : ''}\n\n${fm.foreword}\n\n`;
        if(fm.preface) markdownOutput += `### Preface\n\n${fm.preface}\n\n`;
        if(fm.acknowledgmentsFront) markdownOutput += `### Acknowledgments (Front)\n\n${fm.acknowledgmentsFront}\n\n`;
        if(fm.prologue) markdownOutput += `### Prologue\n\n${fm.prologue}\n\n`;
        if(fm.introduction) markdownOutput += `### Introduction\n\n${fm.introduction}\n\n`;

        // --- MAIN BODY (CHAPTERS) ---
        markdownOutput += "---\n## Main Content\n---\n\n";
        const tempEditorForMarkdown = new Editor({ extensions: [StarterKit] });
        projectState.chapters.forEach((chapter, index) => {
            markdownOutput += `# Chapter ${index + 1}: ${chapter.title}\n\n`;
            
            let contentToExport = '';
            let contentType = '';

            if (projectState.exportChapterContentPreference === 'preview' && chapter.previewContent) {
                contentToExport = chapter.previewContent;
                contentType = `Preview Content (approx. ${chapter.previewWordCountTarget} words)`;
            } else {
                const activeVer = getActiveVersion(chapter);
                if (activeVer) {
                    contentToExport = activeVer.content;
                    contentType = `Full Content (Version ${(chapter.historyIndex ?? 0) + 1})`;
                }
            }
            
            if (contentToExport) {
                markdownOutput += `**_(${contentType})_**\n\n`;
                tempEditorForMarkdown.commands.setContent(contentToExport);
                markdownOutput += `${tempEditorForMarkdown.getText({ blockSeparator: '\n\n', textSerializers: { hardBreak: () => '\n' } })}\n\n`;
            } else {
                 markdownOutput += `_No content available for this chapter based on export preference (${projectState.exportChapterContentPreference})._\n\n`;
            }
        });
        tempEditorForMarkdown.destroy();

        // --- BACK MATTER ---
        markdownOutput += "---\n## Back Matter\n---\n\n";
        const bm = projectState.backMatter;
        if(bm.epilogue) markdownOutput += `### Epilogue\n\n${bm.epilogue}\n\n`;
        if(bm.afterword) markdownOutput += `### Afterword\n\n${bm.afterword}\n\n`;
        if(bm.authorsNote) markdownOutput += `### Author's Note\n\n${bm.authorsNote}\n\n`;
        if(bm.acknowledgmentsBack) markdownOutput += `### Acknowledgments (Back)\n\n${bm.acknowledgmentsBack}\n\n`;
        
        if(bm.appendices && bm.appendices.length > 0) {
            markdownOutput += `### Appendices\n\n${formatListForMarkdown(bm.appendices, 'appendices')}`;
        }
        if(bm.glossary && bm.glossary.length > 0) {
            markdownOutput += `### Glossary\n\n${formatListForMarkdown(bm.glossary, 'glossary')}`;
        }
        if(bm.bibliography && bm.bibliography.length > 0) {
            markdownOutput += `### Bibliography / Works Cited\n\n${formatListForMarkdown(bm.bibliography, 'bibliography')}`;
        }
        
        if(bm.index && bm.index.trim() !== '<!-- Index will be generated here -->') {
            markdownOutput += `### Index\n\n${bm.index}\n\n`;
        }
        if(bm.readingGroupGuide) markdownOutput += `### Reading Group Guide\n\n${bm.readingGroupGuide}\n\n`;
        if(bm.teaserNextBook) markdownOutput += `### Teaser for Next Book\n\n${bm.teaserNextBook}\n\n`;
        if(bm.aboutTheAuthor) markdownOutput += `### About The Author\n\n${bm.aboutTheAuthor}\n\n`;
        if(bm.contactSocialMedia) markdownOutput += `### Contact / Social Media\n\n${bm.contactSocialMedia}\n\n`;
        if(bm.backCoverImageBase64) markdownOutput += `[Back Cover Image was present in project]\n\n`;
        
        // --- REFERENCE SECTIONS (OPTIONAL, but useful for the author) ---
        markdownOutput += "\n---\n## Reference Information (Not for final publication)\n---\n\n";
        
        markdownOutput += "### Author Details (Full)\n\n";
        if(ad.authorName) markdownOutput += `**Author:** ${ad.authorName}\n`;
        if (ad.authorBio) markdownOutput += `**Bio:**\n${ad.authorBio}\n`;
        if (ad.authorWebsite) markdownOutput += `**Website:** ${ad.authorWebsite}\n`;
        if (ad.authorSocialMedia) markdownOutput += `**Social Media:**\n${ad.authorSocialMedia}\n`;
        if (ad.authorContact) markdownOutput += `**Contact:** ${ad.authorContact}\n\n`;

        markdownOutput += "### Book Metadata (Summary)\n\n";
        const metadata = projectState.bookMetadata; 
        (Object.keys(metadata) as Array<keyof BookMetadata>).forEach(key => { 
            const value = metadata[key];
            if (value || (typeof value === 'number' && value !== 0) ) { 
                const label = String(key).replace(/([A-Z](?=[a-z]))|([A-Z]+(?=[A-Z][a-z]))/g, ' $1$2').replace(/^./, str => str.toUpperCase()); 
                markdownOutput += `**${label}**: ${value}\n`; 
            } 
        }); 
        markdownOutput += "\n### World Building & Core Concepts (Summary)\n\n";
        const wd = projectState.blueprint.worldBuildingSuggestions;
        if (wd.logLine) markdownOutput += `**Logline:** ${wd.logLine}\n`;
        if (wd.storyStartConcept) markdownOutput += `**Story Start Concept:** ${wd.storyStartConcept}\n`;
        if (wd.storyEndConcept) markdownOutput += `**Story End Concept:** ${wd.storyEndConcept}\n\n`;
        if (wd.characters.length > 0) {
            markdownOutput += "**Characters:**\n";
            wd.characters.forEach(c => markdownOutput += `- **${c.name}:** ${c.description}\n`);
            markdownOutput += "\n";
        }
        if (wd.locations.length > 0) {
            markdownOutput += "**Locations:**\n";
            wd.locations.forEach(l => markdownOutput += `- **${l.name}:** ${l.briefDescription}\n`);
            markdownOutput += "\n";
        }
        if (wd.customCategories.length > 0) {
            markdownOutput += "**Custom World Elements:**\n";
            wd.customCategories.forEach(cat => {
                markdownOutput += `  - **Category: ${cat.categoryName}\n`;
                cat.items.forEach(item => {
                    markdownOutput += `  - **${item.name}:** ${item.description}\n`;
                });
            });
            markdownOutput += "\n";
        }

        markdownOutput += "### Publishing Essentials (Notes & Drafts)\n\n";
        const pe = projectState.publishingEssentials;
        (Object.keys(pe) as Array<keyof PublishingEssentials>).forEach(key => { 
            if(pe[key as keyof PublishingEssentials]) {
                const label = String(key).replace(/([A-Z](?=[a-z]))|([A-Z]+(?=[A-Z][a-z]))/g, ' $1$2').replace(/^./, str => str.toUpperCase()); 
                markdownOutput += `#### ${label}\n\n${pe[key as keyof PublishingEssentials]}\n\n`;
            }
        });

        return markdownOutput;
    }, [projectState]);

    const handleExportMarkdown = useCallback(() => {
        if (!projectState || (projectState.chapters.length === 0 && !projectState.outlineIdea.trim())) {
            setError("No content to export."); 
            toast({ title: "Nothing to Export", description: "The project is empty.", variant: "destructive" });
            return;
        }
        
        buildMarkdownManuscript(projectState).then(markdownOutput => {
            const blob = new Blob([markdownOutput], { type: 'text/markdown;charset=utf-8' }); 
            const url = URL.createObjectURL(blob); 
            const a = document.createElement('a'); 
            a.href = url; 
            const fileNameBase = String(projectState?.blueprint.worldBuildingSuggestions.selectedStoryTitle || projectState?.currentProjectName || 'writer_studio_export').replace(/\s+/g, '_');
            a.download = `${fileNameBase}_FullManuscript.md`; 
            document.body.appendChild(a); 
            a.click(); 
            document.body.removeChild(a); 
            URL.revokeObjectURL(url);
            toast({ title: "Success", description: `Full Manuscript Markdown exported! File: "${a.download}" saved to your downloads folder.` });
        });
    }, [projectState, setError, toast, buildMarkdownManuscript]);

    const handleExportJson = useCallback(async () => {
        if (!projectState || (!projectState.outlineIdea.trim() && projectState.chapters.length === 0)) {
            setError("No project data to export."); 
            toast({ title: "Nothing to Export", description: "The project is empty.", variant: "destructive" });
            return;
        }
        try {
            const stateToExport: ProjectState = { ...projectState, isUnsaved: false }; // Ensure exported state is "saved"
            const jsonString = JSON.stringify(stateToExport, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const fileNameBase = String(projectState.currentProjectName || projectState.blueprint.worldBuildingSuggestions.selectedStoryTitle || projectState.outlineIdea?.substring(0,20) || 'writer_studio_project');
            a.download = `${fileNameBase.replace(/\s+/g, '_')}.json`;
            a.href = url; 
            document.body.appendChild(a); 
            a.click(); 
            document.body.removeChild(a); 
            URL.revokeObjectURL(url);
            toast({ title: "Success", description: `Project exported successfully! File: "${a.download}" saved to your downloads folder.` });
        } catch (e: any) {
            console.error("Error exporting project to JSON:", e);
            setError(`Failed to export project as JSON: ${e.message}`);
            toast({ title: "Error", description: `Failed to export project as JSON: ${e.message}`, variant: "destructive" });
        }
    }, [projectState, setError, toast]);

    const handleImportJson = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]; if (!file) return;
        if (!confirmationLogic.confirmOverallDiscardChanges("importing a new project (this will replace the current one)")) {
            event.target.value = ''; return;
        }
        setLoadingState("importProject", true); setError(null);
        try {
            const fileContent = await file.text();
            localStorage.setItem(LOCAL_STORAGE_ACTIVE_PROJECT_KEY, fileContent); 
            const hydratedState = loadInitialProjectState(); 
            const fullyHydratedState: ProjectState = {
                ...initialProjectState, 
                ...hydratedState,       
                bookMetadata: { ...initialProjectState.bookMetadata, ...hydratedState.bookMetadata },
                blueprint: {
                    ...initialProjectState.blueprint,
                    ...hydratedState.blueprint,
                    worldBuildingSuggestions: {
                        ...initialProjectState.blueprint?.worldBuildingSuggestions,
                        ...(hydratedState as any).worldBuildingData, // backward compat with old JSON format
                        ...hydratedState.blueprint?.worldBuildingSuggestions,
                    },
                },
                authorDetails: { ...initialAuthorDetails, ...hydratedState.authorDetails },
                frontMatter: { ...initialFrontMatter, ...hydratedState.frontMatter },
                backMatter: { 
                    ...initialBackMatter, 
                    ...hydratedState.backMatter,
                    appendices: hydratedState.backMatter?.appendices ? [...hydratedState.backMatter.appendices] : [],
                    glossary: hydratedState.backMatter?.glossary ? [...hydratedState.backMatter.glossary] : [],
                    bibliography: hydratedState.backMatter?.bibliography ? [...hydratedState.backMatter.bibliography] : [],
                },
                publishingEssentials: { ...initialPublishingEssentials, ...hydratedState.publishingEssentials },
                chapters: (hydratedState.chapters || []).map(ch => ({ 
                    ...ch,
                    vitals: { ...initialChapterVitals, ...ch.vitals }
                })),
                selectedChapterIdsForMerge: hydratedState.selectedChapterIdsForMerge ? [...hydratedState.selectedChapterIdsForMerge] : [],
                isUnsaved: false, 
            };
            setProjectState(fullyHydratedState); 
            queryClient.invalidateQueries(); // Invalidate queries to reflect new project context if needed
            toast({ title: "Success", description: `Project "${fullyHydratedState.currentProjectName || 'Imported Project'}" imported successfully!` });
            const newBlueprintReady = !!fullyHydratedState.hasInitialBlueprint;
            setMainView(determineInitialViewBasedOnState(fullyHydratedState, newBlueprintReady));
        } catch (e: any) {
            console.error("Error importing project from JSON:", e);
            setError(`Failed to import project: ${e.message}. Ensure the file is a valid Writer Studio JSON export.`);
            toast({ title: "Error", description: `Failed to import project: ${e.message}. Ensure the file is a valid Writer Studio JSON export.`, variant: "destructive" });
        } finally {
            setLoadingState("importProject", false);
            event.target.value = ''; 
        }
    }, [confirmationLogic, setLoadingState, setError, setProjectState, setMainView, determineInitialViewBasedOnState, toast, queryClient]);

    const handleBuildForPublishing = useCallback(async () => {
        if (!projectState || (projectState.chapters.length === 0 && !projectState.outlineIdea.trim())) {
            setError("No content to build for publishing."); 
            toast({ title: "Nothing to Build", description: "The project is empty.", variant: "destructive" });
            return;
        }

        try {
            const title = projectState.blueprint.worldBuildingSuggestions.selectedStoryTitle ||
                         projectState.currentProjectName || 
                         projectState.outlineIdea || 
                         'My Work';

            // Generate PDF if selected
            if (projectState.publishingEssentials.mediumFormat === 'PDF' ||
                projectState.publishingEssentials.mediumFormat === 'both') {
                const pdfBlob = await generatePdf(projectState);
                const pdfUrl = URL.createObjectURL(pdfBlob);
                const a = document.createElement('a');
                a.href = pdfUrl;
                a.download = `${title.replace(/\s+/g, '_')}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(pdfUrl);
            }

            // Generate EPUB if selected
            if (projectState.publishingEssentials.mediumFormat === 'EPUB' ||
                projectState.publishingEssentials.mediumFormat === 'both') {
                const epubBlob = await generateEpub(projectState);
                const epubUrl = URL.createObjectURL(epubBlob);
                const a = document.createElement('a');
                a.href = epubUrl;
                a.download = `${title.replace(/\s+/g, '_')}.epub`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(epubUrl);
            }

            toast({ title: "Success", description: 'Publishing package ready! Check your downloads.' });
        } catch (err: any) {
            setError(err instanceof Error ? err.message : String(err));
            toast({ title: "Build Error", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
        }
    }, [projectState, setError, toast]);

    const handleExportKindle = useCallback(async () => {
        if (!projectState || (projectState.chapters.length === 0 && !projectState.outlineIdea.trim())) {
            setError("No content to export for Kindle."); 
            toast({ title: "Nothing to Export", description: "The project is empty.", variant: "destructive" });
            return;
        }

        try {
            
            // Default sections for Kindle (simplified for KDP)
            const kindleSelectedSections = {
                frontMatter: true,
                chapters: true,
                backMatter: true
            };

            await generateAndDownloadKindleEpub(projectState, kindleSelectedSections);
            
            toast({ 
                title: "Kindle Export Complete", 
                description: "Your Kindle-optimized EPUB has been downloaded. Ready for Amazon KDP!"
            });
        } catch (error: any) {
            console.error('❌ [KINDLE-EXPORT] Export failed:', error);
            setError(`Kindle export failed: ${error.message}`);
            toast({ 
                title: "Kindle Export Error", 
                description: `Failed to generate Kindle EPUB: ${error.message}`, 
                variant: "destructive" 
            });
        }
    }, [projectState, setError, toast]);

    const handleSaveProject = useCallback(async () => {
        if (!projectState || !projectState.projectId) {
            toast({ title: "Error", description: "No project to save or project ID is missing.", variant: "destructive" });
            return;
        }
        setLoadingState('save', true);
        try {
            // Prepare project data with the correct field mapping
            const projectDataToSave = {
                ...projectState,
                isUnsaved: false,
                // Ensure the idea field is properly mapped
                idea: projectState.outlineIdea,
            };

            await apiRequest(`/api/story-projects/${projectState.projectId}`, 'PUT', {
                projectData: projectDataToSave,
                title: projectState.currentProjectName || projectState.outlineIdea.substring(0, 50),
                description: projectState.outlineIdea,
                bookType: normalizeBookType(projectState.bookMetadata?.genreSubject || 'NOVEL'),
                targetWordCount: getTargetWordCount(coreState?.dbProject),
                status: 'active',
            });
            setProjectState(prev => prev ? { ...prev, isUnsaved: false } : null);
            toast({ title: "Success", description: "Project saved successfully!" });
            // Invalidate all relevant caches for this project
            queryClient.invalidateQueries({ queryKey: ['projects'] }); // Invalidate project list
            queryClient.invalidateQueries({ queryKey: ['story-project', projectState.projectId] }); // Legacy invalidation
            queryClient.invalidateQueries({ queryKey: ['story-project-summary', projectState.projectId] }); // Optimized data cache
            queryClient.invalidateQueries({ queryKey: ['project-blueprint', projectState.projectId] }); // Blueprint cache
            queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] }); // Summaries list
        } catch (error: any) {
            setError(`Save Error: ${error.message}`);
            toast({ title: "Error", description: `Failed to save project: ${error.message}`, variant: "destructive" });
        } finally {
            setLoadingState('save', false);
        }
    }, [projectState, setLoadingState, setError, setProjectState, toast, queryClient, coreState]);

    const handleDeleteProject = useCallback(async () => {
        if (!projectState || !projectState.projectId) {
            toast({ title: "Error", description: "No project to delete or project ID is missing.", variant: "destructive" });
            return;
        }
        if (!confirmationLogic.confirmOverallDiscardChanges("deleting this project permanently from the server")) {
            return;
        }

        setLoadingState('delete', true);
        try {
            await apiRequest(`/api/story-projects/${projectState.projectId}`, 'DELETE');
            localStorage.removeItem(LOCAL_STORAGE_ACTIVE_PROJECT_KEY);
            setProjectState(null);
            toast({ title: "Success", description: "Project deleted successfully." });
            queryClient.invalidateQueries({ queryKey: ['projects'] }); // Invalidate project list
            setMainView('ideaHub'); // Go back to idea hub after deletion
        } catch (error: any) {
            setError(`Delete Error: ${error.message}`);
            toast({ title: "Error", description: `Failed to delete project: ${error.message}`, variant: "destructive" });
        } finally {
            setLoadingState('delete', false);
        }
    }, [projectState, confirmationLogic, setLoadingState, setError, setProjectState, toast, setMainView, queryClient]);

    return {
        handleSaveApiKey,
        handleGenerateCover,
        handleGenerateBookImage, // 🆕 Enhanced book image generation with format specifications
        handleExportMarkdown,
        handleExportJson,
        handleImportJson,
        handleBuildForPublishing,
        handleExportKindle,
        handleSaveProject,
        handleDeleteProject,
    };
};